// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { Link } from '@calendly/ui/components/link';
import { Tab, Tabs } from '@calendly/ui/components/tabs';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { findKey, keyBy } from 'lodash-es';
import { forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation, Link as RouterLink } from 'react-router-dom';
import { useProductRefreshSettingsIa } from 'features/app_shell/product_refresh/useProductRefreshSettingsIa';
import { SETTINGS_SEARCH_PARAM, SETTINGS_TAB_SEARCH_PARAM, SETTINGS_SECTION } from 'features/settings_modal/constants';
import { SETTINGS_ADMIN_PEOPLE_SUBNAV, SETTINGS_ORG_DIRECTORY_SUBNAV } from 'features/settings_modal/settingsConfig';
import { resolveSettingsNavigationTarget } from 'features/settings_modal/use_settings_navigation';
import { hasEnforcedDomainsSelector } from 'selectors/current_organization';
import { totalInvitationCountSelector, totalMigrationCountSelector, totalUserCountSelector } from './selectors';
import { isAdminOrOwnerSelector, isGroupAdminSelector } from '../../selectors/current_user';
const addCounterToString = (string, counter) => {
  if (typeof counter !== 'number') return string;
  return `${string} (${counter})`;
};

/** Matches {@link SETTINGS_ADMIN_PEOPLE_SUBNAV} slugs — static keys for eslint i18n rules. */
function translatePeopleSubnavLabel(slug) {
  switch (slug) {
    case 'users':
      return I18n.t('publisher.organization.members.member_status.active');
    case 'invitations':
      return I18n.t('publisher.organization.members.member_status.pending');
    case 'migrations':
      return I18n.t('publisher.organization.members.member_status.migrating');
    default:
      return '';
  }
}
const peopleTabSlugToSwitcherScope = slug => {
  if (slug === 'pending_users') return 'invitations';
  if (slug === 'migrating_users') return 'migrations';
  return 'users';
};
const switcherScopeToPeopleSettingsTab = value => {
  if (value === 'users') return 'active_users';
  if (value === 'invitations') return 'pending_users';
  return 'migrating_users';
};
const tabsWrapperCss = "t1ik5c1w";
const TabsWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(tabsWrapperCss, props.className)} />;
});
export const Switcher = () => {
  const location = useLocation();
  const {
    pathname
  } = location;
  const usersCount = useSelector(totalUserCountSelector);
  const invitationsCount = useSelector(totalInvitationCountSelector);
  const migrationsCount = useSelector(totalMigrationCountSelector);
  const hasAppConsolidationFeature = useFlag('calendly_app_consolidation');
  const isRebrandSettingsIaEnabled = useProductRefreshSettingsIa();
  const isUserGroupAdminSelector = useSelector(isGroupAdminSelector);
  const isOwnerOrAdmin = useSelector(isAdminOrOwnerSelector);
  const hasNewAdminIALayout = isUserGroupAdminSelector || isOwnerOrAdmin;
  const isMobile = useDeviceType('isPhone');
  const hasEnforcedDomains = useSelector(hasEnforcedDomainsSelector);
  const settingsSection = new URLSearchParams(location.search).get(SETTINGS_SEARCH_PARAM);
  const settingsPeopleTabSlug = new URLSearchParams(location.search).get(SETTINGS_TAB_SEARCH_PARAM);
  const useSettingsQueryTabs = isRebrandSettingsIaEnabled && (settingsSection === SETTINGS_SECTION.adminPeople || settingsSection === SETTINGS_SECTION.orgDirectory);
  const peopleSubnavRows = useMemo(() => {
    const rows = settingsSection === SETTINGS_SECTION.orgDirectory ? SETTINGS_ORG_DIRECTORY_SUBNAV : SETTINGS_ADMIN_PEOPLE_SUBNAV;
    return rows.filter(row => !row.onlyWithAppConsolidation || hasAppConsolidationFeature);
  }, [hasAppConsolidationFeature, settingsSection]);
  const adminPeopleSections = useMemo(() => keyBy(peopleSubnavRows, 'slug'), [peopleSubnavRows]);
  const paths = useMemo(() => {
    const baseBySlug = {
      users: hasNewAdminIALayout ? '/app/admin/users' : '/app/organization/users',
      invitations: hasNewAdminIALayout ? '/app/admin/invitations' : '/app/organization/invitations',
      migrations: hasNewAdminIALayout ? '/app/admin/migrations' : '/app/organization/migrations'
    };
    return Object.fromEntries(peopleSubnavRows.map(({
      slug
    }) => [slug, baseBySlug[slug]]));
  }, [hasNewAdminIALayout, peopleSubnavRows]);
  const countBySlug = {
    users: usersCount,
    invitations: invitationsCount,
    migrations: migrationsCount
  };
  const scope = useSettingsQueryTabs ? peopleTabSlugToSwitcherScope(settingsPeopleTabSlug ?? '') : findKey(paths, path => matchPath(pathname, path));
  const options = peopleSubnavRows.map(({
    slug
  }) => ({
    text: addCounterToString(translatePeopleSubnavLabel(slug), countBySlug[slug]),
    value: slug
  }));
  return <TabsWrapper>
      <Tabs>
        {options.map(option => <Tab key={option.value} selected={scope === option.value} as={RouterLink} to={resolveSettingsNavigationTarget({
        section: adminPeopleSections[option.value].section,
        legacyPath: paths[option.value],
        location,
        isRebrandSettingsIaEnabled,
        settingsTab: switcherScopeToPeopleSettingsTab(option.value)
      })}>
            {option.text}
          </Tab>)}
      </Tabs>
      {isOwnerOrAdmin && hasEnforcedDomains && !isMobile && <Link href="/app/organization/activity_log?filters%5Bactivity%5D%5B%5D=Request_Access&filters%5Bactivity%5D%5B%5D=Invite_Sent">
          {I18n.t('publisher.organization_settings.domain_control.access_requests')}
        </Link>}
    </TabsWrapper>;
};

require("./switcher.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./switcher.js");